import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { SeasonBadge } from './Badges';
import { displayTitle, thumbTarget } from './format';
import { C, MONO, RADIUS } from './tokens';
import OutletTransferDialog from '../OutletTransferDialog';
import { OutletCandidate } from '../merchandisingTypes';
import {
  RestockV2Order, RestockV2OutletLine, RestockV2OutletLot, RestockV2OutletUnmatched,
  RestockV2OutletVariant, RestockV2ThumbTarget,
} from './types';

// 出清架 — the pieces Magnus already paid for that cover part of what this plan would otherwise
// order from Korea. Measured on prod 2026-09-19: 223 sellable pieces at 出清 NT$463 against a
// 定價 of NT$893, 106 of them on styles the rail already carries.
//
// WHY IT SITS ON 訂貨 AND NOWHERE ELSE. The pull needs two OWNER decisions — the 門市售價 the
// piece will carry and whether its 品況 is good enough for the counter — so it belongs beside
// the other 「what do I actually have to buy」 judgements, not on the part-timers' 今天 walk.
// After the transfer the piece is office stock and the existing 從辦公室拿 lane carries it to the
// rail with no new code and no new move kind.
//
// THE ACTION IS THE OLD DIALOG ON PURPOSE. OutletTransferDialog + useMerchandisingWrite already
// carry the idempotency key, the 品況 checkbox, the price field and the retry — and the endpoint
// 409s on a price that disagrees with stock the counter already holds, which is why the engine
// sends `store_price` down rather than letting the screen guess one. A second writer here would
// be a second set of those rules to keep in step.

const money = (value: number | null | undefined): string => (
  value === null || value === undefined ? '—' : `NT$${Math.round(value)}`
);

const variantLabel = (variant: { color: string | null; size: string | null }): string => (
  [variant.color, variant.size].filter(Boolean).join(' / ') || '單一規格'
);

/** One lot → the shape the shared dialog already knows how to write. */
const candidateOf = (
  row: RestockV2OutletLine, variant: RestockV2OutletVariant, lot: RestockV2OutletLot,
): OutletCandidate => ({
  outlet_item_id: Number(lot.outlet_item_id),
  sku: row.sku,
  title: displayTitle(row.title),
  color: variant.color,
  size: variant.size,
  available: lot.qty,
  // `price` is what the dialog offers as 門市售價 — the engine's answer, not the clearance price.
  price: Math.round(Number(variant.store_price ?? lot.list_price ?? 0)),
  outlet_price: lot.outlet_price === null ? undefined : Math.round(Number(lot.outlet_price)),
  suggested_qty: lot.qty,
  image_url: row.image_url ?? null,
  variant_id: variant.variant_id,
  reason: row.why,
  requires_confirmation: true,
});

const LotRow: React.FC<{
  row: RestockV2OutletLine; variant: RestockV2OutletVariant; lot: RestockV2OutletLot;
  enabled: boolean; onPull: (candidate: OutletCandidate) => void;
}> = ({ row, variant, lot, enabled, onPull }) => (
  <Box
    data-outlet-lot={lot.outlet_item_id ?? ''}
    sx={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
      borderTop: '1px solid', borderColor: C.line, py: 0.8,
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 800, minWidth: 96 }}>{variantLabel(variant)}</Typography>
    <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700, color: C.fetch }}>
      {lot.qty} 件
    </Typography>
    <Typography sx={{ fontSize: 11.5, color: C.ink3 }}>
      {lot.slot_number === null ? '未分配貨架格' : `出清架 ${lot.slot_number} 號格`}
    </Typography>
    <Typography sx={{ fontSize: 11.5, color: C.ink2 }}>
      出清 {money(lot.outlet_price)} → 門市 {money(variant.store_price ?? lot.list_price)}
    </Typography>
    <Button
      size="small" disabled={!enabled || !lot.outlet_item_id}
      onClick={() => onPull(candidateOf(row, variant, lot))}
      sx={{ ml: 'auto', flex: 'none' }}
    >
      確認品況與售價
    </Button>
  </Box>
);

const OutletCard: React.FC<{
  row: RestockV2OutletLine; enabled: boolean;
  onPull: (candidate: OutletCandidate) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ row, enabled, onPull, onImage }) => (
  <Box
    component="article" data-outlet={row.sku}
    sx={{ bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: `${RADIUS}px`, p: 1.1, mb: 0.9 }}
  >
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <ProductThumb
        target={thumbTarget({ sku: row.sku, title: row.title, image_url: row.image_url ?? null })}
        width={64} height={85} radius={7} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <SeasonBadge value={row.season} />
          <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
          {displayTitle(row.title)}
        </Typography>
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
        <Typography sx={{ fontSize: 11.5, color: C.ink3 }}>{row.why}</Typography>
      </Box>
      {/* SKU-scoped on purpose: the section carries the same sentence for the whole shelf, and two
          identical labels make every byLabelText lookup ambiguous. */}
      <Box aria-label={`${row.sku} 出清架可抵 ${row.pieces} 件`} sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>可抵</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.1 }}>
          {row.pieces}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
      </Box>
    </Box>
    {row.variants.map((variant) => variant.lots.map((lot) => (
      <LotRow
        key={`${variant.variant_id}-${lot.outlet_item_id}`}
        row={row} variant={variant} lot={lot} enabled={enabled} onPull={onPull}
      />
    )))}
  </Box>
);

/**
 * Owned pieces that could NOT be offered. Rendered for the same reason `unaccounted` is: a piece
 * he paid for may be un-pullable, never unmentioned. Never filtered, never hidden when short.
 */
const UnmatchedFold: React.FC<{ rows: RestockV2OutletUnmatched[] }> = ({ rows }) => {
  if (!rows.length) return null;
  const pieces = rows.reduce((n, row) => n + Number(row.available || 0), 0);
  // Two different things share this fold and the summary says so rather than averaging them: a
  // handful whose colour/size cannot be named (someone has to look), and the long tail of styles
  // the counter plan simply never considered. Measured on prod 2026-09-19: 4 and 87.
  const needsEyes = rows.filter((row) => !/沒有進到補貨清單/.test(row.reason)).length;
  return (
    <Box component="details" data-outlet-unmatched sx={{ mt: 1.2, px: '2px' }}>
      <Typography component="summary" sx={{ fontSize: 12.5, fontWeight: 800, cursor: 'pointer', color: C.ink2 }}>
        出清架沒放進來的 {rows.length} 筆 · {pieces} 件
        {needsEyes > 0 && (
          <Box component="span" sx={{ color: C.back, fontWeight: 700, ml: '5px' }}>
            其中 {needsEyes} 筆要人工確認
          </Box>
        )}
      </Typography>
      {rows.map((row) => (
        <Typography key={`${row.outlet_item_id}-${row.sku}`} sx={{ fontSize: 11.5, color: C.ink3, mt: '3px' }}>
          <Box component="span" sx={{ fontFamily: MONO }}>{row.sku}</Box>
          {' '}{[row.color, row.size].filter(Boolean).join(' / ')}
          {row.slot_number === null ? '' : ` · ${row.slot_number} 號格`}
          {' · '}{row.reason}
        </Typography>
      ))}
    </Box>
  );
};

const OutletSection: React.FC<{
  order: RestockV2Order | undefined;
  enabled: boolean;
  onSaved: () => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ order, enabled, onSaved, onImage }) => {
  const [pull, setPull] = useState<OutletCandidate | null>(null);
  const rows = order?.outlet || [];
  const unmatched = order?.outlet_unmatched || [];
  if (!rows.length && !unmatched.length) return null;
  const pieces = Number(order?.totals?.outlet || 0);
  const saving = Number(order?.totals?.outlet_saving || 0);
  return (
    <Box component="section" aria-label={`出清架可抵 ${pieces} 件`} sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.5 }}>
        <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em' }}>
          出清架
          <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>
            {rows.length} 款 · 已經買了，不用訂
          </Box>
        </Typography>
        <Typography
          sx={{
            ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.fetch,
            bgcolor: C.fetchWash, borderRadius: '99px', px: 1,
          }}
        >
          {pieces} 件
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>
        這些顏色尺寸出清架上就有，上面的韓國訂量已經扣掉了。確認品況與門市售價後會先調到辦公室待送，
        送櫃跟上架走原本的流程。
        {saving > 0 && `　同一批貨用門市售價賣，比出清價多 NT$${Math.round(saving)}。`}
      </Typography>
      {rows.map((row) => (
        <OutletCard key={row.sku} row={row} enabled={enabled} onPull={setPull} onImage={onImage} />
      ))}
      <UnmatchedFold rows={unmatched} />
      {pull && (
        <OutletTransferDialog
          item={pull} enabled={enabled}
          onClose={() => setPull(null)}
          onSaved={() => { setPull(null); onSaved(); }}
        />
      )}
    </Box>
  );
};

export default OutletSection;
